import {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    useAccountingMigration,
    useBulkUpdateImportedAccounts,
    useImportedAccounts,
} from "@api/accountingIntegration.api";
import { useWorkspaceAccountGroups } from "@api/account.api";
import { countImportedAccountMappingResolutions } from "@common/helpers/importedAccountMappingResolution";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { CoaMappingEmptyState } from "./CoaMappingEmptyState";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { getMapToAccountUpdate } from "../accountMappingBulkEligibility";
import { getImportedAccountProvider } from "../importedAccountProvider";
import { buildCoaMappingRows } from "./buildCoaMappingRows";
import { CoaMappingChartEditorDrawer } from "./CoaMappingChartEditorDrawer";
import { CoaMappingCommandIsland } from "./CoaMappingCommandIsland";
import { CoaMappingCreateAccountModal } from "./CoaMappingCreateAccountModal";
import { filterCoaMappingRows } from "./filterCoaMappingRows";
import { CoaMappingRow } from "./coaMappingRowTypes";
import { useCoaMappingTableMeta } from "./useCoaMappingTableMeta";
import { useChartEditorSession } from "./useChartEditorSession";
import { useMappingRowCreation } from "./useMappingRowCreation";
import { useUnmappedRowNavigation } from "./useUnmappedRowNavigation";
import { CoaMappingPreviewStep } from "./preview/CoaMappingPreviewStep";
import {
    buildCoaMappingView,
    CoaMappingColumn,
    createCoaMappingViewConfig,
} from "./coaMappingViewConfig";
import styles from "./CoaMappingDrawer.module.scss";

interface Props {
    workspaceId: string;
    entityId: number;
    nextTaskId: string | undefined;
    onClose: () => void;
    onChartEdited: () => void;
    escapeInterceptorRef: MutableRefObject<(() => void) | null>;
}

type MappingStep = "map" | "preview";

export const CoaMappingDrawer = ({
    workspaceId,
    entityId,
    nextTaskId,
    onClose,
    onChartEdited,
    escapeInterceptorRef,
}: Props) => {
    const [step, setStep] = useState<MappingStep>("map");
    const {
        data: importedAccountsData,
        isError,
        refetch: refetchAccounts,
    } = useImportedAccounts(workspaceId, entityId, { orderBy: "name" });
    const { data: accountingMigration } = useAccountingMigration(
        workspaceId,
        entityId,
        { refetchUntilMappingFinished: true },
    );
    const {
        accounts: kickAccounts,
        accountsById: kickAccountsById,
        isLoading: isKickAccountsLoading,
    } = useAccounts({
        entityId,
        includeDisabled: true,
    });
    const { data: kickGroupsByEntityId } = useWorkspaceAccountGroups();
    const kickGroups = useMemo(
        () => kickGroupsByEntityId?.get(entityId) ?? [],
        [kickGroupsByEntityId, entityId],
    );
    const hasKickGroups = kickGroups.length > 0;
    const view = useMemo(
        () => buildCoaMappingView({ hasKickGroups }),
        [hasKickGroups],
    );
    const showAccountNumbers = useShowAccountNumbers();
    const { mutate: bulkUpdate, isPending: isBulkUpdatePending } =
        useBulkUpdateImportedAccounts(workspaceId, entityId);

    const isAccountsMappingRunning =
        !!accountingMigration && !accountingMigration.accountsMappedAt;

    useEffect(() => {
        if (!isAccountsMappingRunning) {
            refetchAccounts();
        }
    }, [refetchAccounts, isAccountsMappingRunning]);

    const rows = useMemo<CoaMappingRow[] | null>(
        () =>
            importedAccountsData
                ? buildCoaMappingRows({
                      accounts: importedAccountsData.accounts,
                      groups: importedAccountsData.accountGroups,
                  })
                : null,
        [importedAccountsData],
    );

    const [searchFilter, setSearchFilter] = useState<string | undefined>();
    const visibleRows = useMemo(
        () => (rows ? filterCoaMappingRows({ rows, searchFilter }) : null),
        [rows, searchFilter],
    );
    const counts = useMemo(
        () =>
            countImportedAccountMappingResolutions({
                accounts: importedAccountsData?.accounts ?? [],
            }),
        [importedAccountsData],
    );
    const clearSearch = useCallback(() => setSearchFilter(undefined), []);
    const { tableRef, unmappedRows, currentIndex, navigateTo } =
        useUnmappedRowNavigation({ rows, visibleRows, clearSearch });

    const viewConfig = useMemo(
        () =>
            createCoaMappingViewConfig({
                providerLabel: getImportedAccountProvider(
                    accountingMigration?.externalSystem,
                ).label,
            }),
        [accountingMigration?.externalSystem],
    );

    const onMapAccount = useCallback(
        ({
            importedAccountId,
            accountId,
        }: {
            importedAccountId: string;
            accountId: string | null;
        }) => {
            bulkUpdate({
                updates: [
                    getMapToAccountUpdate({ id: importedAccountId, accountId }),
                ],
            });
        },
        [bulkUpdate],
    );

    const importedAccounts = importedAccountsData?.accounts;
    const { createModal, closeCreateModal, onOpenCreateAccountModal } =
        useMappingRowCreation({ importedAccounts, kickAccountsById });

    const { isEditorOpen, openEditor, closeEditor, refreshAfterChartEdits } =
        useChartEditorSession({
            workspaceId,
            entityId,
            importedAccounts,
            kickAccountsById,
            refetchImportedAccounts: refetchAccounts,
        });

    const tableMeta = useCoaMappingTableMeta({
        entityId,
        isCreateAccountAvailable:
            !isAccountsMappingRunning && !isKickAccountsLoading,
        isAccountsMappingRunning,
        kickAccountsById,
        kickGroups,
        showAccountNumbers,
        onMapAccount,
        onOpenCreateAccountModal,
    });

    const footer = (
        <CoaMappingCommandIsland
            isLoaded={
                importedAccountsData !== undefined && !isKickAccountsLoading
            }
            isMappingPending={isBulkUpdatePending}
            isCreateAccountModalOpen={createModal !== undefined}
            isChartEditorOpen={isEditorOpen}
            importedCount={importedAccountsData?.accounts.length ?? 0}
            counts={counts}
            currentUnmappedIndex={currentIndex}
            currentUnmappedName={unmappedRows[currentIndex]?.name}
            onNavigateUnmapped={navigateTo}
            onClose={onClose}
            onEditChart={openEditor}
            onPreview={() => setStep("preview")}
        />
    );
    // First child of both steps so React keeps it mounted across the switch.
    const editorDrawer = (
        <CoaMappingChartEditorDrawer
            isOpen={isEditorOpen}
            workspaceId={workspaceId}
            entityId={entityId}
            sourceRows={rows}
            sourceViewConfig={viewConfig}
            importedCount={importedAccountsData?.accounts.length ?? 0}
            counts={counts}
            onClose={closeEditor}
            onChartEdited={onChartEdited}
            refreshAfterChartEdits={refreshAfterChartEdits}
        />
    );

    if (step === "preview") {
        return (
            <div className={styles.drawerContent}>
                {editorDrawer}
                <div className={styles.subtitle}>
                    Step 2 of 2 · Review chart of accounts
                </div>
                <CoaMappingPreviewStep
                    entityId={entityId}
                    nextTaskId={nextTaskId}
                    accountingMigration={accountingMigration}
                    importedAccounts={importedAccounts ?? []}
                    sourceRows={rows}
                    sourceViewConfig={viewConfig}
                    kickAccounts={kickAccounts}
                    kickGroups={kickGroups}
                    onBack={() => setStep("map")}
                    escapeInterceptorRef={escapeInterceptorRef}
                />
            </div>
        );
    }

    return (
        <div className={styles.drawerContent}>
            {editorDrawer}
            <div className={styles.subtitle}>Step 1 of 2 · Map accounts</div>
            {isError ? (
                <EmptyState header="Couldn't load imported accounts. Please try again." />
            ) : (
                <>
                    <TablePageHeader
                        isFullPage
                        borderBottom
                        search={searchFilter}
                        onSearchChange={(value) =>
                            setSearchFilter(value || undefined)
                        }
                        searchKeyboardShortcuts={false}
                    />
                    <div className={styles.tableContainer}>
                        <ViewTable<CoaMappingRow, CoaMappingColumn, never>
                            ref={tableRef}
                            data={visibleRows}
                            view={view}
                            config={viewConfig}
                            tableMeta={tableMeta}
                            autoExpandOnMount
                            nestingIndentPx={16}
                            enableColumnResizing
                            emptyView={() => (
                                <CoaMappingEmptyState
                                    searchFilter={searchFilter}
                                />
                            )}
                            footer={footer}
                        />
                    </div>
                </>
            )}
            {createModal && (
                <CoaMappingCreateAccountModal
                    account={createModal.account}
                    initialValues={createModal.initialValues}
                    entityId={entityId}
                    onMapAccount={onMapAccount}
                    onClose={closeCreateModal}
                />
            )}
        </div>
    );
};
